import React from 'react';
import { format } from 'date-fns';
import clsx from 'clsx';
import { Event, eventGroupLabel } from '../../types/event';
import { getWeekDays, getTimeSlots, getEventPosition, formatEventTime, HUNGARIAN_WEEKDAYS, getEventColor, isAllDayEvent } from '../../utils/calendar';

interface WeekViewProps {
  currentDate: Date;
  events: Event[];
  onEventClick: (event: Event) => void;
  onTimeSlotClick: (date: Date, time?: string) => void;
  canModifyEvents: boolean;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  onEventClick,
  onTimeSlotClick,
  canModifyEvents,
}) => {
  const { days } = getWeekDays(currentDate, events);
  const timeSlots = getTimeSlots();

  const handleTimeSlotClick = (date: Date, timeSlot: string) => {
    if (!canModifyEvents) return;

    const [hours, minutes] = timeSlot.split(':').map(Number);
    const slotDate = new Date(date);
    slotDate.setHours(hours, minutes, 0, 0);
    onTimeSlotClick(slotDate, timeSlot);
  };

  return (
    <div className="h-full overflow-auto bg-white">
      {/* Mobilon vízszintesen görgethető, a fejléc és az idősáv ragadós */}
      <div className="min-w-[640px] md:min-w-full flex flex-col">
        {/* Header */}
        <div className="flex border-b border-gray-200 sticky top-0 z-30 bg-white md:static">
          <div className="w-16 flex-shrink-0 sticky left-0 z-10 bg-white"></div>
        {days.map((day, index) => (
          <div
            key={index}
            className="flex-1 text-center p-3 border-r border-gray-200 last:border-r-0"
          >
            <div className="text-sm font-medium text-gray-700">
              {HUNGARIAN_WEEKDAYS[index]}
            </div>
            <div
              className={clsx(
                'text-lg font-semibold mt-1',
                {
                  'text-blue-600': day.isToday,
                  'text-gray-900': !day.isToday,
                }
              )}
            >
              {format(day.date, 'd')}
            </div>
            {day.isToday && (
              <div className="w-6 h-6 bg-blue-600 text-white rounded-full mx-auto mt-1 flex items-center justify-center text-xs font-bold">
                {format(day.date, 'd')}
              </div>
            )}
          </div>
        ))}
      </div>

        {/* Time Grid */}
        <div className="flex">
          {/* Time Column */}
          <div className="w-16 flex-shrink-0 sticky left-0 z-20 bg-white">
            {timeSlots.map((timeSlot) => (
              <div
                key={timeSlot}
                className="h-15 border-b border-gray-200 text-xs text-gray-500 p-1 text-right"
                style={{ height: '60px' }}
              >
                {timeSlot}
              </div>
            ))}
          </div>

          {/* Days Columns */}
          {days.map((day, dayIndex) => (
            <div
              key={dayIndex}
              className="flex-1 border-r border-gray-200 last:border-r-0 relative"
            >
              {/* Time Slots */}
              {timeSlots.map((timeSlot) => (
                <div
                  key={timeSlot}
                  className="h-15 border-b border-gray-100 cursor-pointer hover:bg-gray-50 relative"
                  style={{ height: '60px' }}
                  onClick={() => handleTimeSlotClick(day.date, timeSlot)}
                >
                  {/* Half-hour line */}
                  <div className="absolute top-1/2 left-0 right-0 border-t border-gray-100"></div>
                </div>
              ))}

              {/* Events */}
              {day.events.map((event) => {
                const position = getEventPosition(event, day.date);
                return (
                  <div
                    key={event.id}
                    className={clsx(
                      'absolute left-1 right-1 rounded p-1 cursor-pointer hover:opacity-80 z-10 border transition-opacity',
                      getEventColor(event.type)
                    )}
                    style={{
                      top: `${position.top}px`,
                      height: `${Math.max(position.height, 20)}px`,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                    title={`${event.title}${eventGroupLabel(event) ? ` - ${eventGroupLabel(event)}` : ''}${event.location ? ` - ${event.location}` : ''} - ${formatEventTime(event)}`}
                  >
                    <div className="text-xs font-medium truncate">
                      {event.title}
                      {eventGroupLabel(event) && <span className="opacity-75"> ({eventGroupLabel(event)})</span>}
                    </div>
                    {!isAllDayEvent(event) && position.height > 25 && (
                      <div className="text-xs opacity-75 truncate">
                        {formatEventTime(event)}
                      </div>
                    )}
                    {event.location && position.height > 40 && (
                      <div className="text-xs opacity-60 truncate">
                        📍 {event.location}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WeekView;